import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { displayDate } from "../../../../helper-functions";
import { TissueOffer } from "../../../../models";
import { useSurgerySchedulerSurgeonQuery } from "../../../../queries";
import { Lists } from "../../../lists";
import { DataTable } from "../../../../component-library";
import { TissueOfferModal } from "../../../tissue-offers/TissueOfferModal";
import { usePermissions } from "../../../../contexts";

interface PendingTissueOffersProps {
    tissueOffers: TissueOffer[];
}

export function PendingTissueOffers({ tissueOffers }: Readonly<PendingTissueOffersProps>) {
    const { isSurgeryUser } = usePermissions();
    const {
        mySurgeonsQuery: { data },
    } = useSurgerySchedulerSurgeonQuery({ queryType: "mySurgeons" });
    const [selectedOfferId, setSelectedOfferId] = useState<number>(0);
    const selectedOffer = tissueOffers.find((o) => o.id === selectedOfferId);

    const showSurgeonColumn = !isSurgeryUser || (!!data && data.length > 1);

    const columns = useMemo<ColumnDef<TissueOffer>[]>(() => {
        const result: ColumnDef<TissueOffer>[] = [];

        if (showSurgeonColumn) {
            result.push({
                accessorKey: "surgeon.lastName",
                header: "Surgeon",
                cell: ({ row: { original: offer } }) => {
                    const surgeon = offer.surgeon;
                    if (!surgeon) return "-";
                    return surgeon?.lastName ? `${surgeon.lastName}, ${surgeon?.firstName ?? ""}` : "-";
                },
            });
        }

        result.push(
            ...([
                {
                    accessorKey: "surgery.scheduledOnUtc",
                    header: "Surgery Date/Time",
                    cell: ({ row: { original: offer } }) =>
                        offer.surgery?.scheduledOnUtc
                            ? new Date(offer.surgery.scheduledOnUtc).toLocaleString("en-US", {
                                  month: "2-digit",
                                  day: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                              })
                            : "-",
                },
                {
                    accessorKey: "surgery.recipientLastName",
                    header: "Patient",
                    cell: ({ row: { original: offer } }) => {
                        const surgery = offer.surgery;
                        if (!surgery) return "-";
                        return surgery.recipientLastName
                            ? `${surgery.recipientLastName}, ${surgery.recipientFirstName ?? ""}`
                            : "-";
                    },
                },
                {
                    accessorKey: "surgery.surgeryType.name",
                    header: "Surgery Type",
                    cell: ({ row: { original: offer } }) => {
                        const surgery = offer.surgery;
                        if (!surgery) return "-";
                        return surgery.surgeryType ? surgery.surgeryType.name : "-";
                    },
                },
                {
                    accessorKey: "tissueId",
                    header: "Tissue",
                    cell: ({ row: { original: offer } }) => {
                        return offer.tissue?.number ?? "-";
                    },
                },
                {
                    accessorKey: "surgeryId",
                    id: "view-surgery",
                    header: "",
                    cell: ({ row: { original: offer } }) => {
                        if (!offer.surgery) return "";
                        return (
                            <div className="text-end me-2">
                                <Link
                                    to={`/lists/${Lists.SurgeriesForDate}/${displayDate(offer.surgery.scheduledOnUtc)}/surgeries/${offer.surgeryId}`}
                                    className="btn btn-sm btn-primary "
                                >
                                    View Surgery
                                </Link>
                            </div>
                        );
                    },
                },
                {
                    accessorKey: "id",
                    id: "view-offer",
                    header: "",
                    cell: ({ row: { original: offer } }) => {
                        return (
                            <div className="text-end">
                                <Button size="sm" onClick={() => setSelectedOfferId(offer.id)}>
                                    View Offer
                                </Button>
                            </div>
                        );
                    },
                },
            ] as ColumnDef<TissueOffer>[])
        );
        return result;
    }, [showSurgeonColumn]);

    return (
        <div>
            {tissueOffers.length ? (
                <DataTable columns={columns} data={tissueOffers} paginated={tissueOffers.length > 10} />
            ) : (
                <div>No tissue offers found.</div>
            )}

            <TissueOfferModal
                show={!!selectedOfferId}
                onHide={() => setSelectedOfferId(0)}
                tissueOffer={selectedOffer}
            />
        </div>
    );
}
